import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { ResendConfirmation } from '../settings/ResendConfirmation';
import { EMAIL_ANCHOR, noticeView } from '../settings/emailView';

/** How often a page left open asks again: the drain works every minute. */
export const EMAIL_BANNER_POLL_MS = 5 * 60_000;

/**
 * The app-wide email banner (MAP 249 spec §9, ticket 07): above every office page, for the owner
 * and the office, the MOST URGENT thing about the shop's email (`email.status`'s first notice):
 * paused, held, the owner's email unconfirmed, the domain failing, the mailbox to reconnect, or
 * emails waiting (a check, a reply address, today's limit, a limit outside the shop). Its link
 * lands on the exact control in Settings → Email that answers it, scrolled to and marked; the
 * rest are listed there ("+N more").
 *
 * An owner whose own email is unconfirmed gets "Send it again" right here: that is how a shop's
 * existing owners confirm the day email is switched on.
 *
 * ⚠️ It says nothing it does not know. No answer yet, a failed one, or none offline: no banner
 * (it never says "all good" either, so its absence claims nothing). In-flow under the shell's
 * chrome, never floating over a page's controls.
 */
export function EmailBanner() {
  const { role } = useSession();
  const reads = role === 'owner' || role === 'office';
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.email.status.queryOptions(),
    // The bookkeeper and the tech are not asked about it (the server would refuse them).
    enabled: reads,
    refetchInterval: EMAIL_BANNER_POLL_MS,
  });
  const first = reads ? q.data?.notices[0] : undefined;
  if (!q.data || !first) return null;
  const v = noticeView(first, q.data);
  const more = q.data.notices.length - 1;
  return (
    <div className="px-4 pt-3 md:px-6">
      <div role="status" data-email-banner={v.kind} className={`notice badge--${v.tone}`}>
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="min-w-0">{v.text}</span>
          {v.resend ? <ResendConfirmation /> : null}
          <Link
            to="/settings"
            hash={v.link.hash}
            className="font-medium underline underline-offset-2"
          >
            {v.link.label}
          </Link>
          {more > 0 ? (
            <Link
              to="/settings"
              hash={EMAIL_ANCHOR.status}
              className="underline underline-offset-2"
            >
              {more === 1 ? '1 more about email' : `${more} more about email`}
            </Link>
          ) : null}
        </p>
      </div>
    </div>
  );
}
